<script>
import DataLayer from '@services/data-layer/index'
import GenreOptions from '@components/genre/genre-options'

export default {
    components: {
        GenreOptions,
    },
    props: {
        text: {
            type: String,
            required: true,
        },
        resource: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            isFollowing: false,
            isReady: true,
        }
    },
    computed: {
        isLoggedIn() {
            return this.$store.getters['auth/loggedIn']
        },
        currentUserId() {
            return this.$store.state.auth.currentUser?.members_id?.id
        },
    },
    watch: {
        resource: {
            immediate: true,
            handler(newResource) {
                if (newResource) {
                    this.checkIfFollowing()
                }
            },
        },
    },
    methods: {
        async checkIfFollowing() {
            if (!this.isLoggedIn || !this.resource || !this.currentUserId) {
                this.isFollowing = false
                return
            }

            try {
                const response = await DataLayer.favorites.getAll({
                    type: 'genres',
                    membersId: this.currentUserId,
                })
                const followedGenres = response.body || []
                this.isFollowing = followedGenres.some(item => item.genres_id?.id === this.resource.id)
            } catch (error) {
                console.error('Error checking genre follow status:', error)
                this.isFollowing = false
            }
        },
        async toggleFollow() {
            if (!this.isLoggedIn) {
                this.$router.push({ name: 'login' })
                return
            }

            if (!this.isReady || !this.resource) return

            this.isReady = false

            try {
                if (this.isFollowing) {
                    await DataLayer.favorites.remove({
                        type: 'genres',
                        membersId: this.currentUserId,
                        id: this.resource.id,
                    })
                    this.isFollowing = false
                    this.$store.dispatch('appearance/handleNotification', {
                        message: 'Genre Unfollowed',
                        type: 'success',
                    })
                } else {
                    await DataLayer.favorites.add({
                        type: 'genres',
                        membersId: this.currentUserId,
                        id: this.resource.id,
                    })
                    this.isFollowing = true
                    this.$store.dispatch('appearance/handleNotification', {
                        message: 'Following Genre',
                        type: 'success',
                    })
                }
            } catch (error) {
                console.error('Error toggling genre follow:', error)
                this.$store.dispatch('appearance/handleNotification', {
                    message: this.$m('SOMETHING_WENT_WRONG'),
                    type: 'warning',
                })
            } finally {
                this.isReady = true
            }
        },
    },
}
</script>

<template>
    <div class="container">
        <div class="inner">
            <div>
                <h6 class="type">Genre</h6>
                <BasePlaceholder
                    class="title"
                    :text="text"
                    type="genre"
                    tag="h1"
                />
                <div
                    v-if="resource"
                    class="actions"
                >
                    <BaseButton
                        :active="isFollowing"
                        button-type="follow"
                        size="large"
                        @click="toggleFollow"
                    >
                        {{ isFollowing ? 'Following' : 'Follow' }}
                    </BaseButton>
                    <div
                        class="options"
                    >
                        <BaseDropdown >
                            <BaseButton
                                slot="trigger"
                                class="trigger-button"
                                :modifiers="{ autoWidth: false, transparent: false, dots: true }"
                                button-type="tag"
                                aria-label="Options"
                                size="large"
                            />
                            <div slot="dropdown">
                                <GenreOptions
                                    :genre="resource"
                                    :is-following="isFollowing"
                                    :has-view-genre-option="false"
                                    @toggle-follow="toggleFollow"
                                />
                            </div>
                        </BaseDropdown>
                    </div>
                </div>
            </div>
        </div>
        <BaseHeroBackground
            v-if="resource"
            :resource="resource"
            type="artwork"
        />
    </div>
</template>

<style lang="scss" scoped>
@import '@design';

.container {
    position: relative;
    z-index: $layer-highlight-z-index;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding-top: $s-xl;

    @include desktop {
        min-height: 350px;
    }
}

.inner {
    position: relative;
    z-index: $layer-page-z-index;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    width: 100%;

    @include container;

    @include tablet {
        flex-direction: row;
    }

    @include desktop {
        margin-top: $s-h * 0.3;
    }

    @include sidePadding;
}

.type {
    display: none;

    @include tablet {
        display: block;
    }
}

.title {
    @extend %h3;

    width: 100%;
    margin-bottom: $s-xxl;
    text-align: left;

    @include tablet {
        @include h1;

        margin-top: $s-xxxl;
    }
}

.actions {
    display: flex;
    gap: $s-m;
    justify-content: center;
    margin-top: $s-l;

    @include tablet {
        justify-content: flex-start;
    }
}

.options {
    position: relative;
}

.trigger-button {
    width: 40px;
}
</style>
